import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, EyeOff, Trash2, Loader2, Shield, MessageSquare,
  Mail, Phone, Calendar, ChevronRight, Check
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const AdminContacts = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadMessages();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error) setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await supabase.from('contact_messages').update({ is_read: true }).eq('id', id);
      await loadMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(language === 'tr' ? 'Bu mesajı silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this message?')) {
      return;
    }
    try {
      await supabase.from('contact_messages').delete().eq('id', id);
      setSelectedMessage(null);
      await loadMessages();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const texts = {
    tr: {
      title: 'İletişim Mesajları',
      noMessages: 'Henüz mesaj yok',
      from: 'Gönderen',
      subject: 'Konu',
      date: 'Tarih',
      markRead: 'Okundu İşaretle',
      delete: 'Sil',
      unread: 'Okunmamış',
      read: 'Okunmuş',
      message: 'Mesaj',
      phone: 'Telefon',
      email: 'E-posta',
      selectMessage: 'Detayları görmek için bir mesaj seçin'
    },
    en: {
      title: 'Contact Messages',
      noMessages: 'No messages yet',
      from: 'From',
      subject: 'Subject',
      date: 'Date',
      markRead: 'Mark as Read',
      delete: 'Delete',
      unread: 'Unread',
      read: 'Read',
      message: 'Message',
      phone: 'Phone',
      email: 'Email',
      selectMessage: 'Select a message to view details'
    }
  };

  const t = texts[language] || texts.tr;

  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-orange-600" />
      </div>
    );
  }

  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-orange-600 text-white rounded-xl">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="admin-contacts-page">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-orange-600 dark:hover:text-orange-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/admin" className="hover:text-orange-600 dark:hover:text-orange-400">
              Admin
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">{t.title}</span>
          </nav>

          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-orange-600" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
            <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-medium rounded-full">
              {messages.filter(m => !m.is_read).length} {t.unread}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t.noMessages}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1 space-y-3">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={() => setSelectedMessage(msg)}
                  className={`bg-white dark:bg-gray-900 border rounded-xl p-4 cursor-pointer transition-all ${
                    selectedMessage?.id === msg.id 
                      ? 'border-orange-500 ring-2 ring-orange-500/20' 
                      : 'border-gray-200 dark:border-gray-800 hover:border-orange-300'
                  } ${!msg.is_read ? 'border-l-4 border-l-orange-500' : ''}`}
                  data-testid={`message-item-${msg.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className={`font-medium ${!msg.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                      {msg.full_name}
                    </p>
                    {!msg.is_read && (
                      <span className="w-2 h-2 bg-orange-500 rounded-full" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate mb-2">
                    {msg.subject || msg.message?.substring(0, 50)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(msg.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedMessage ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl"
                >
                  {/* Header */}
                  <div className="p-6 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                          {selectedMessage.full_name}
                        </h2>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {selectedMessage.email}
                          </span>
                          {selectedMessage.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {selectedMessage.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(selectedMessage.created_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!selectedMessage.is_read && (
                          <button
                            onClick={() => markAsRead(selectedMessage.id)}
                            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                            title={t.markRead}
                          >
                            <Check className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(selectedMessage.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title={t.delete}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  {selectedMessage.subject && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t.subject}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedMessage.subject}</p>
                    </div>
                  )}

                  {/* Message Content */}
                  <div className="p-6">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t.message}</p>
                    <div className="prose dark:prose-invert max-w-none">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </div>
                  </div>

                  {/* Reply Action */}
                  <div className="p-6 border-t border-gray-200 dark:border-gray-800">
                    <a
                      href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Your Message'}`}
                      className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {language === 'tr' ? 'E-posta ile Yanıtla' : 'Reply via Email'}
                    </a>
                  </div>
                </motion.div>
              ) : (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">{t.selectMessage}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
